import { Injectable } from '@angular/core';
import { Logger } from '../../core/services/logger.service';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, firstValueFrom, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  map,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface City {
  location?: {
    lat: number;
    lng: number;
  };
  // Propriétés correspondant au backend
  id?: string;
  cityCode: string;
  zipCode: string;
  label: string;
  lat?: number;
  lng?: number;
  // Propriétés pour la rétrocompatibilité
  city_code?: string;
  zip_code?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CitiesService {
  private readonly logger = new Logger(CitiesService.name);
  private readonly apiUrl = `${environment.apiUrl}/api/cities`;
  private searchTerms = new Subject<string>();

  constructor(private httpClient: HttpClient) {}

  searchCitiesWithDebounce(debounceMs = 300, limit = 20): Observable<City[]> {
    return this.searchTerms.pipe(
      debounceTime(debounceMs),
      distinctUntilChanged(),
      map((term: string) => term.trim()),
      switchMap((term: string) => {
        if (!term?.trim() || term.length < 2) return of([]);

        return this.httpClient
          .get<{ data: City[] }>(`${this.apiUrl}/autocomplete`, {
            params: {
              term: term,
              limit: limit.toString(),
            },
          })
          .pipe(
            switchMap(response => of(this.normalizeCities(response.data))),
            catchError(error => {
              this.logger.error('Error searching cities', error);
              return of([]);
            })
          );
      })
    );
  }

  search(term: string): void {
    this.searchTerms.next(term);
  }

  async searchCities(searchTerm: string, limit = 20): Promise<City[]> {
    if (!searchTerm?.trim() || searchTerm.length < 2) return [];

    try {
      const response = await firstValueFrom(
        this.httpClient.get<{ data: City[] }>(`${this.apiUrl}/autocomplete`, {
          params: {
            term: searchTerm,
            limit: limit.toString(),
          },
        })
      );

      // Normaliser les données pour maintenir la compatibilité avec le code existant
      return this.normalizeCities(response.data);
    } catch (error) {
      this.logger.error('Error searching cities', error);
      return [];
    }
  }

  async getCityByZipCode(zipCode: string): Promise<City | undefined> {
    if (!zipCode?.trim() || zipCode.length < 2) return undefined;

    try {
      const response = await firstValueFrom(
        this.httpClient.get<{ data: City[] }>(
          `${this.apiUrl}/zipcode/${zipCode}`
        )
      );

      if (response.data && response.data.length > 0) {
        return this.normalizeCity(response.data[0]);
      }
      return undefined;
    } catch (error) {
      this.logger.error('Error getting city by zip code', error);
      return undefined;
    }
  }

  async findCityByCodeAndZipCode(
    cityCode: string,
    zipCode: string
  ): Promise<City | undefined> {
    if (!zipCode?.trim() || zipCode.length < 2) return undefined;

    try {
      const response = await firstValueFrom(
        this.httpClient.get<{ data: City[] }>(
          `${this.apiUrl}/zipcode/${zipCode}`
        )
      );

      const city = response.data.find(city => city.cityCode === cityCode);
      return city ? this.normalizeCity(city) : undefined;
    } catch (error) {
      this.logger.error('Error finding city by code and zip code', error);
      return undefined;
    }
  }

  private normalizeCity(city: City): City {
    return {
      ...city,
      location: {
        lat: city.lat || 0,
        lng: city.lng || 0,
      },
      city_code: city.cityCode,
      zip_code: city.zipCode,
    };
  }

  private normalizeCities(cities: City[]): City[] {
    return cities.map(city => this.normalizeCity(city));
  }
}
